<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, toRef } from 'vue';
import { useProjectCreation } from '../use/project';
import { useAuth } from '../../composables/useAuth';
import { useAuthModal } from '../../composables/useAuthModal';
import { Textarea } from '@seede/ui';
import {
  PROMPT_CACHE_KEY,
  clearCreateChatStateCache,
  loadCreateChatStateCache,
  saveCreateChatStateCache,
  serializeChatStateAttachments,
} from '@/lib/create-chat-state-cache';
import localStorage from '@/lib/local-storage';
import { Amplitude } from '@seede/amplitude';
import { i18n, t } from '~/i18n';

const locale = toRef(i18n.options, 'locale');

const placeholderPrefix = computed(() => {
  void locale.value;
  return t('landing_home.hero_prompt.placeholder');
});

const btnDesignLabel = computed(() => {
  void locale.value;
  return t('landing_home.hero_prompt.btn_design');
});

const btnDesigningLabel = computed(() => {
  void locale.value;
  return t('landing_home.hero_prompt.btn_designing');
});

const { createProject, isSubmitting } = useProjectCreation();
const { isAuthenticated } = useAuth();
const { openAuthModal } = useAuthModal();

const prompt = ref('');
const isRedirecting = ref(false);
const showSweep = ref(false);
const isInputFocused = ref(false);
let sweepInterval: ReturnType<typeof setInterval> | null = null;

const textareaRef = ref(null);

const chatState = ref({
  assets: [] as Array<Record<string, unknown>>,
  documents: [] as Array<Record<string, unknown>>,
});

const hasPromptOrAttachments = computed(() => {
  const hasPrompt = !!prompt.value.trim();
  const hasAssets = (chatState.value.assets?.length ?? 0) > 0;
  const hasDocs = (chatState.value.documents?.length ?? 0) > 0;
  return hasPrompt || hasAssets || hasDocs;
});

const showBusy = computed(() => isSubmitting.value || isRedirecting.value);
const isDisabled = computed(() => showBusy.value || !hasPromptOrAttachments.value);

const restoreChatStateFromCache = () => {
  const cached = loadCreateChatStateCache();
  if (!cached) return;
  chatState.value.assets = cached.assets;
  chatState.value.documents = cached.documents;
};

// 触发渐变扫过效果
const triggerSweep = () => {
  if (isInputFocused.value) return;
  showSweep.value = false;
  setTimeout(() => {
    showSweep.value = true;
  }, 50);
};

const handleInputFocus = () => {
  isInputFocused.value = true;
  showSweep.value = false;
};

const handleInputBlur = () => {
  isInputFocused.value = false;
};

// 组件加载后，每隔 5 秒触发一次渐变扫过效果
onMounted(() => {
  if (import.meta.client) {
    prompt.value = localStorage.getItem(PROMPT_CACHE_KEY) || '';
    restoreChatStateFromCache();
  }

  // 首次延迟 300ms 触发
  setTimeout(() => {
    triggerSweep();
  }, 300);

  // 每隔 5 秒重复触发
  sweepInterval = setInterval(() => {
    triggerSweep();
  }, 10000);
});

onUnmounted(() => {
  if (sweepInterval) {
    clearInterval(sweepInterval);
  }
});

watch(prompt, (newValue) => {
  if (import.meta.client) {
    localStorage.setItem(PROMPT_CACHE_KEY, newValue);
  }
});

watch(
  chatState,
  (state) => {
    if (import.meta.client) saveCreateChatStateCache(state);
  },
  { deep: true }
);

const handleSubmit = async () => {
  Amplitude.event('landing_create_project_click', {
    surface: 'hero_prompt_input',
    prompt_length: prompt.value.length,
  });
  if (!hasPromptOrAttachments.value || showBusy.value) return;

  let inputPrompt = prompt.value.trim();
  inputPrompt += serializeChatStateAttachments(chatState.value, inputPrompt);

  if (!isAuthenticated.value) {
    saveCreateChatStateCache(chatState.value);
    openAuthModal('sign-up', () => {
      restoreChatStateFromCache();
      handleSubmit();
    });
    return;
  }

  isRedirecting.value = true;

  try {
    const project = await createProject({ prompt: inputPrompt }, { creationMode: 'ai_generate' });

    prompt.value = '';
    chatState.value.assets = [];
    chatState.value.documents = [];
    if (import.meta.client) {
      clearCreateChatStateCache();
    }

    handleProjectCreated(project.id);
  } catch (error) {
    isRedirecting.value = false;
    Amplitude.event('landing_create_project_failed', {
      surface: 'hero_prompt_input',
      error_message: (error as Error)?.message?.slice(0, 120) ?? '',
    });
    console.error('Failed to create project:', error);
  }
};

const handleProjectCreated = (projectId: string) => {
  const projectUrl = new URL(location.href);
  projectUrl.pathname = `/design/${projectId}`;

  // Clear persisted draft so returning to the homepage starts fresh,
  // while keeping prompt in memory until the page unloads.
  localStorage.removeItem(PROMPT_CACHE_KEY);

  navigateTo(projectUrl.href, {
    external: true,
  });
};
</script>

<template>
  <div class="w-full input-wrapper">
    <div
      class="prompt-input-container rounded-3xl bg-white backdrop-blur-[10px] border-[0.5px] border-black/15 overflow-hidden shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]"
      :class="{ 'sweep-active': showSweep }"
    >
      <Textarea
        ref="textareaRef"
        v-model="prompt"
        :placeholder-prefix="placeholderPrefix"
        :chatState="chatState"
        class="bg-transparent"
        @keydown.enter.meta="handleSubmit"
        @keydown.enter.ctrl="handleSubmit"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
      />
      <div class="px-2 pb-2 flex items-center justify-end">
        <button
          type="button"
          :disabled="isDisabled"
          class="px-4 py-2 rounded-full bg-[#ff6b35] text-white font-medium text-sm hover:bg-[#ff7d4d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          @click="handleSubmit"
        >
          <span v-if="showBusy">{{ btnDesigningLabel }}</span>
          <span v-else>{{ btnDesignLabel }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Gradient sweep effect */
.prompt-input-container {
  position: relative;
}

.prompt-input-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 107, 53, 0.15) 10%,
    rgba(247, 201, 75, 0.2) 25%,
    rgba(74, 222, 128, 0.2) 40%,
    rgba(34, 211, 238, 0.2) 55%,
    rgba(167, 139, 250, 0.15) 70%,
    rgba(244, 114, 182, 0.1) 85%,
    transparent 100%
  );
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  z-index: 1;
}

.prompt-input-container.sweep-active::before {
  opacity: 1;
  animation: gradient-sweep 0.4s ease-out forwards;
}

@keyframes gradient-sweep {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

/* 确保内容在渐变之上 */
.prompt-input-container > * {
  position: relative;
  z-index: 2;
}

.input-wrapper {
  max-width: clamp(500px, 50vw, 1100px);
}
</style>
